import {gql, QueryOptions} from '@apollo/client';
import Backbone from 'backbone';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ApolloFileState from 'filestate/ApolloFileState';
import {FileStateAttributes} from 'filestate/FileState';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {agentFoundryPageUrl} from 'llm/routes/UrlUtils';
import * as navigation from 'Navigation';

import {
  AgentFoundryFileStateQuery,
  AgentFoundryFileStateQueryVariables,
} from './__generated/AgentFoundryFileStateQuery';
import AgentFoundryTabState from './AgentFoundryTabState';

export const AGENT_FOUNDRY_FILE_STATE_QUERY = gql`
  query AgentFoundryFileStateQuery($agentFoundryId: StrictString!) @skipAudit @engComponent(name: LLM) {
    agentFoundry(id: $agentFoundryId) {
      ... on AgentFoundryNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface AgentFoundryFileStateAttributes {
  name?: string;
  hideTabNav?: boolean;
}

class AgentFoundryFileState extends ApolloFileState<
  AgentFoundryFileStateQuery,
  AgentFoundryFileStateQueryVariables,
  AgentFoundryFileStateAttributes
> {
  static fileType = ModelTypeConstants.AGENT_FOUNDRY;

  static override getQueryOptions(fileId: string): QueryOptions<AgentFoundryFileStateQueryVariables> {
    return {
      query: AGENT_FOUNDRY_FILE_STATE_QUERY,
      variables: {agentFoundryId: fileId},
    };
  }

  static override dataToAttributes(data: AgentFoundryFileStateQuery): AgentFoundryFileStateAttributes {
    const {agentFoundry} = data;
    return agentFoundry == null || isInaccessible(agentFoundry) ? {} : {name: agentFoundry.name};
  }

  static override openFile(fileId: string): JQueryPromise<AgentFoundryFileState> {
    return super.openFile(fileId) as JQueryPromise<AgentFoundryFileState>;
  }

  agentFoundryTab!: AgentFoundryTabState;

  override defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.SingleBufferStatusBarState(),
      hideTabNav: true,
    };
  }

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  override initialize(...args: any): void {
    super.initialize(...args);

    this.agentFoundryTab = new AgentFoundryTabState({
      agentFoundryId: this.id,
    });

    const buffers = new navigation.BufferStates([
      new navigation.BufferState(),
      new navigation.BufferState({active: false}),
    ]);
    this.set({buffers});
    const buffer = buffers.at(0);
    buffer.tabs().add(this.agentFoundryTab);
    buffer.tabs().at(0).set({active: true});
  }

  override editURL(): string {
    return agentFoundryPageUrl(this.id);
  }

  override name(): string {
    return this.get('name') ?? 'Loading...';
  }

  override registerTitleAndURLSync(): void {
    this.on('change:name', () => {
      this.syncTitleAndURL();
    });
  }
}

export default AgentFoundryFileState;
